/**
 * Esquemas de escritura de Contratos.
 *
 * El ciclo de vida del contrato es el corazón del encargo, así que cada operación tiene su
 * propio esquema en vez de un `PATCH` genérico: activar, renovar y rescindir NO son ediciones
 * de campos, son transiciones con reglas propias. Un `PATCH { status: "activo" }` se saltaría
 * la generación de cargos y dejaría la unidad marcada como libre.
 */
import { z } from 'zod';
import {
  zIsoDate,
  zMoneyNonZero,
  zMoneyPositive,
  zOptionalText,
  zPaymentDay,
  zPercent,
  zUuid,
} from '../validation/primitives';

export const LEASE_STATUSES = ['borrador', 'activo', 'vencido', 'rescindido', 'renovado'] as const;
export const DEPOSIT_STATUSES = ['pendiente', 'depositado', 'devuelto', 'retenido'] as const;
export const INDEXATION_TYPES = ['ninguna', 'ipc', 'fija'] as const;

export const TERMINATION_REASONS = [
  'fin_contrato',
  'acuerdo_mutuo',
  'impago',
  'incumplimiento',
  'venta_inmueble',
  'obra',
  'otro',
] as const;

export const TERMINATION_REASON_LABELS: Record<(typeof TERMINATION_REASONS)[number], string> = {
  fin_contrato: 'Fin de contrato',
  acuerdo_mutuo: 'Acuerdo mutuo',
  impago: 'Impago',
  incumplimiento: 'Incumplimiento de condiciones',
  venta_inmueble: 'Venta del inmueble',
  obra: 'Necesidad de obra',
  otro: 'Otro motivo',
};

export const LEASE_EVENT_TYPES = [
  'creado',
  'firmado',
  'activado',
  'renovado',
  'rescindido',
  'vencido',
  'fianza_depositada',
  'fianza_devuelta',
  'renta_actualizada',
  'titular_anadido',
  'titular_retirado',
  'renovacion_anulada',
] as const;

// ---------------------------------------------------------------------------
// Alta
// ---------------------------------------------------------------------------

const leaseConditions = z.object({
  startDate: zIsoDate,
  endDate: zIsoDate.nullable().optional(),
  monthlyRent: zMoneyNonZero,
  depositAmount: zMoneyPositive.optional(),
  /** Adelanto mensual de suministros. Se regulariza cada cuatrimestre con el gasto real. */
  utilityAdvance: zMoneyPositive.optional(),
  paymentDay: zPaymentDay.optional(),
  /** Preaviso legal en días. Alimenta la alerta de vencimiento. */
  noticePeriodDays: z.number().int().min(0).max(365).optional(),
  indexationType: z.enum(INDEXATION_TYPES).optional(),
  /** Porcentaje aplicado en la renovación. Solo tiene sentido si `indexationType` no es `ninguna`. */
  indexationValue: zPercent.nullable().optional(),
  signedAt: zIsoDate.nullable().optional(),
  notes: zOptionalText(4000),
});

function conditionsCoherent(
  value: {
    startDate?: string;
    endDate?: string | null;
    indexationType?: string;
    indexationValue?: number | null;
  },
  ctx: z.RefinementCtx,
): void {
  if (value.startDate && value.endDate && value.endDate <= value.startDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['endDate'],
      message: 'La fecha de fin debe ser posterior a la de inicio.',
    });
  }
  if (
    value.indexationType &&
    value.indexationType !== 'ninguna' &&
    (value.indexationValue === null || value.indexationValue === undefined)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['indexationValue'],
      message: 'Indica el porcentaje de actualización.',
    });
  }
}

export const createLeaseSchema = leaseConditions
  .extend({
    unitId: zUuid,
    /**
     * Titulares del contrato. Al menos uno, y exactamente uno principal — si el cliente no
     * marca ninguno, el servicio marca el primero (ver `services/leases.ts`).
     */
    tenants: z
      .array(
        z.object({
          tenantId: zUuid,
          isPrimary: z.boolean().optional(),
        }),
      )
      .min(1, 'Un contrato necesita al menos un titular.')
      .max(8, 'Máximo 8 titulares solidarios.'),
    /**
     * Activarlo en la misma petición: es lo que hace «Asignar habitación», porque poner a
     * alguien en una habitación es que ya vive ahí. Sin él, nace en borrador como siempre.
     */
    activate: z.boolean().optional(),
  })
  .superRefine((value, ctx) => {
    conditionsCoherent(value, ctx);

    const ids = value.tenants.map((entry) => entry.tenantId);
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tenants'],
        message: 'Hay un titular repetido.',
      });
    }
    if (value.tenants.filter((entry) => entry.isPrimary).length > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tenants'],
        message: 'Solo puede haber un titular principal.',
      });
    }
  });

/**
 * La habitación que se le da a alguien EN SU ALTA de inquilino: las mismas condiciones que un
 * contrato, sin los titulares, porque el titular es la persona que se está creando. Se activa
 * siempre: darle habitación es que ya vive ahí.
 */
export const tenantRoomSchema = leaseConditions
  .extend({
    unitId: zUuid,
    /**
     * El día en que SE FUE, según su hoja de rescisión: quien se da de alta con un contrato
     * antiguo que no llegó a cumplir. Su estancia acaba ese día —y no choca con quien entró
     * después—; el contrato queda rescindido, sin cobros si ya pasó.
     */
    exitDate: zIsoDate.optional(),
  })
  .superRefine((value, ctx) => {
    conditionsCoherent(value, ctx);
    if (value.exitDate && value.exitDate <= value.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['exitDate'],
        message: 'La salida tiene que ser posterior a la entrada.',
      });
    }
  });

/**
 * Edición. En `borrador` se puede tocar todo; con el contrato ya `activo` la ruta restringe a
 * los campos que no falsean el histórico contable (ver `LEASE_ACTIVE_EDITABLE_FIELDS`).
 */
export const updateLeaseSchema = leaseConditions.partial().superRefine(conditionsCoherent);

/**
 * Cambiar de habitación un contrato —también de inmueble—. Es una CORRECCIÓN de dónde vive,
 * no un contrato nuevo: renta, fechas y cargos emitidos se quedan como están. Se admite con el
 * contrato en borrador o activo; la habitación de destino tiene que estar libre en sus fechas.
 */
export const moveLeaseUnitSchema = z.object({ unitId: zUuid });

/**
 * Campos que siguen siendo editables con el contrato activo. La renta NO está: cambiarla
 * reescribiría el importe que respalda cargos ya emitidos. Para subir la renta se renueva.
 */
export const LEASE_ACTIVE_EDITABLE_FIELDS = [
  'endDate',
  // No falsea ningún cargo emitido: solo cambia lo que se espera en la próxima regularización.
  'utilityAdvance',
  'noticePeriodDays',
  'indexationType',
  'indexationValue',
  'signedAt',
  'notes',
] as const;

// ---------------------------------------------------------------------------
// Transiciones
// ---------------------------------------------------------------------------

export const activateLeaseSchema = z.object({
  /** Fecha desde la que se emiten cargos. Por defecto, la de inicio del contrato. */
  effectiveAt: zIsoDate.optional(),
  signedAt: zIsoDate.nullable().optional(),
});

export const renewLeaseSchema = leaseConditions
  .partial()
  .extend({
    /**
     * Renta de la renovación. Si no se envía, el servicio la calcula indexando la anterior:
     * `renta × (1 + indexationValue/100)` redondeado a 2 decimales.
     */
    monthlyRent: zMoneyNonZero.optional(),
    startDate: zIsoDate,
    endDate: zIsoDate.nullable().optional(),
  })
  .superRefine(conditionsCoherent);

export const terminateLeaseSchema = z.object({
  reason: z.enum(TERMINATION_REASONS),
  /** Fecha efectiva de salida. Los cargos posteriores se anulan o se prorratean. */
  effectiveAt: zIsoDate,
  notes: zOptionalText(2000),
  /**
   * Confirmación escrita del código de la unidad, al estilo de los borrados peligrosos de
   * GitHub. La rescisión anula cargos y libera la unidad: no debe poder hacerse de un clic.
   */
  confirmation: z.string().trim().min(1, 'Escribe el código de la unidad para confirmar.'),
});

export const updateDepositSchema = z
  .object({
    status: z.enum(DEPOSIT_STATUSES),
    amount: zMoneyPositive.optional(),
    depositedAt: zIsoDate.nullable().optional(),
    returnedAt: zIsoDate.nullable().optional(),
    registryRef: zOptionalText(80),
  })
  .superRefine((value, ctx) => {
    if (value.status === 'depositado' && !value.depositedAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['depositedAt'],
        message: 'Indica la fecha de ingreso de la fianza.',
      });
    }
    if (value.status === 'devuelto' && !value.returnedAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['returnedAt'],
        message: 'Indica la fecha de devolución.',
      });
    }
  });

export const addLeaseTenantSchema = z.object({
  tenantId: zUuid,
  isPrimary: z.boolean().optional(),
});

/**
 * Al quitar al titular principal hay que designar a otro EN LA MISMA petición. Un contrato sin
 * principal no tiene interlocutor y rompe el índice `one_primary_tenant_per_lease` en cuanto
 * alguien intente arreglarlo después.
 */
export const removeLeaseTenantSchema = z.object({
  newPrimaryTenantId: zUuid.optional(),
});

export type CreateLeaseInput = z.input<typeof createLeaseSchema>;
export type TenantRoomInput = z.input<typeof tenantRoomSchema>;
export type UpdateLeaseInput = z.input<typeof updateLeaseSchema>;
export type RenewLeaseInput = z.input<typeof renewLeaseSchema>;
export type TerminateLeaseInput = z.input<typeof terminateLeaseSchema>;
export type UpdateDepositInput = z.input<typeof updateDepositSchema>;

/**
 * Indexación de la renta. Vive en `shared` porque el asistente de renovación tiene que
 * MOSTRAR el cálculo (`1.100 € × 1,021 = 1.123,10 €`) antes de enviarlo, y el servidor tiene
 * que llegar exactamente al mismo número.
 *
 * Se opera sobre enteros de céntimos: `1100 * 1.021` en coma flotante da 1123.0999999999999,
 * y `toFixed(2)` sobre eso redondea bien por casualidad, no por diseño.
 */
export function indexRent(currentRent: string, percent: number): string {
  const cents = Math.round(Number(currentRent) * 100);
  const updated = Math.round((cents * (10_000 + Math.round(percent * 100))) / 10_000);
  return (updated / 100).toFixed(2);
}
